/**
 * The Place on map preview: the same least-squares similarity the converter fits (API T15/R18), run in the browser so
 * the user sees RMS and check error before placing. The converter's numbers are the ones that count; this only previews.
 * ArcGIS-free so karma can run it.
 */
export interface PlacePair {
  /** Page space (R19), 0–1 from the top-left. */
  sheet: { x: number; y: number };
  /** WGS84 */
  map: { lat: number; lon: number };
  /** A check point: kept out of the fit and measured against it */
  check?: boolean;
}

export interface SimilarityFit {
  /** Metres per page pixel */
  scale: number;
  /** Degrees, counter-clockwise */
  rotation: number;
  /** Metres in the local frame (east, north of the control points' centroid) */
  tx: number;
  ty: number;
  rms_m: number;
  /** The worst check point, or null without one */
  check_error_m: number | null;
}

/** WGS84 semi-major axis and first eccentricity squared */
const WGS84_A = 6378137;
const WGS84_E2 = 0.00669437999014;
const DEG = Math.PI / 180;

/**
 * Fits E = a·u − b·v + tx, N = b·u + a·v + ty over the control points. Sheet points are page pixels with the row axis
 * flipped (u = x·width, v = −y·height), as the converter does, because a similarity cannot absorb the y-down reflection
 * and normalised coordinates on a non-square page are not a similarity frame. Map points go to metres east/north of the
 * control centroid on the WGS84 ellipsoid: north by the meridional radius M, east by N·cos φ. A single spherical
 * radius would be ~0.4% anisotropic at mid latitudes, which a similarity cannot absorb, while the converter's UTM frame
 * is conformal. Null below `minControl` control points (3; placing by stations fits 2 exactly, as the API's derived
 * third point does) or for a degenerate fit.
 */
export function fitSimilarity(pairs: PlacePair[], page: { width: number; height: number }, minControl = 3): SimilarityFit | null {
  const control = pairs.filter(p => !p.check);
  if (control.length < Math.max(2, minControl) || !(page?.width > 0) || !(page?.height > 0)) {
    return null;
  }
  const lat0 = control.reduce((s, p) => s + p.map.lat, 0) / control.length;
  const lon0 = control.reduce((s, p) => s + p.map.lon, 0) / control.length;
  const sin0 = Math.sin(lat0 * DEG);
  const w = Math.sqrt(1 - WGS84_E2 * sin0 * sin0);
  const meridional = (WGS84_A * (1 - WGS84_E2)) / (w * w * w);
  const parallel = (WGS84_A / w) * Math.cos(lat0 * DEG);
  const local = (p: PlacePair) => ({
    u: p.sheet.x * page.width,
    v: -p.sheet.y * page.height,
    e: (p.map.lon - lon0) * DEG * parallel,
    n: (p.map.lat - lat0) * DEG * meridional,
  });
  const pts = control.map(local);
  const mean = (key: 'u' | 'v' | 'e' | 'n') => pts.reduce((s, p) => s + p[key], 0) / pts.length;
  const [mu, mv, me, mn] = [mean('u'), mean('v'), mean('e'), mean('n')];
  let ss = 0;
  let sa = 0;
  let sb = 0;
  let sm = 0;
  for (const p of pts) {
    const [u, v, e, n] = [p.u - mu, p.v - mv, p.e - me, p.n - mn];
    ss += u * u + v * v;
    sa += u * e + v * n;
    sb += u * n - v * e;
    sm += e * e + n * n;
  }
  // Coincident sheet points, or map points with no spread (a zero-scale fit).
  if (ss < 1e-9 || sm < 1e-6) {
    return null;
  }
  const a = sa / ss;
  const b = sb / ss;
  const tx = me - a * mu + b * mv;
  const ty = mn - b * mu - a * mv;
  const residual = (p: ReturnType<typeof local>) => Math.hypot(a * p.u - b * p.v + tx - p.e, b * p.u + a * p.v + ty - p.n);
  const rms = Math.sqrt(pts.reduce((s, p) => s + residual(p) ** 2, 0) / pts.length);
  const checks = pairs.filter(p => p.check).map(p => residual(local(p)));
  return {
    scale: Math.hypot(a, b),
    rotation: Math.atan2(b, a) / DEG,
    tx,
    ty,
    rms_m: rms,
    check_error_m: checks.length ? Math.max(...checks) : null,
  };
}
